<template>
  <Teleport to="body">
    <div v-if="mounted" class="fixed inset-0 z-[60]" @click="close()" />
    <div
      v-if="mounted"
      ref="bubble"
      role="menu"
      class="fixed z-[61] origin-center overflow-hidden border bg-[#fbfbfb]/80 backdrop-blur-xl"
      :class="closing && 'pointer-events-none'"
    >
      <div ref="content" class="absolute w-[322px] pt-2.5 pb-3.5">
        <div ref="pageEl">
          <UploadMenuOptions
            :page="page"
            :can-register="canStartUpload"
            @page="switchPage"
            @register="handleRegister"
            @checker="close(openChecker)"
            @report="close(openReport)"
          />
        </div>
      </div>
      <div
        v-if="fromFab"
        ref="facade"
        class="pointer-events-none absolute inset-0 flex items-center justify-center bg-green-900"
      >
        <FileAddFilledIcon class="size-[24px] text-white" />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { FileAddFilledIcon } from '@dacr/icons'

import { useUploadFlow } from '@/features/upload/composables/useUploadFlow'
import { useEventListener } from '@/shared/composables/useEventListener'
import { useInfringementTools } from '@/shared/composables/useInfringementTools'

import UploadMenuOptions, { type UploadMenuPage } from './UploadMenuOptions.vue'
import { MENU_SPRING, QUICK_SPRING, Spring, easeInOutQuad } from './spring'

const props = withDefaults(
  defineProps<{
    anchor?: HTMLElement | null
    origin?: HTMLElement | null
    initialPage?: UploadMenuPage
  }>(),
  { anchor: null, origin: null, initialPage: 'actions' }
)

const open = defineModel<boolean>('open', { required: true })

const MENU_WIDTH = 322
const CARD_RADIUS = 38
const MARGIN = 8
const GAP = 8
const FAB_SIZE = 50
const BAR_OVERHANG = 6
const FALLBACK_BAR_BOTTOM = 20
const PULL_DOWN_MAX_START_WIDTH = 96
const PAGE_SLIDE = 24

interface Box {
  width: number
  height: number
  right: number
  bottom: number
  radius: number
}

interface Placement {
  left: number
  top: number
  width: number
  height: number
  scale: number
  above: boolean
}

const { showUploadDialog, canStartUpload } = useUploadFlow()
const { openChecker, openReport } = useInfringementTools()

const bubble = ref<HTMLElement>()
const content = ref<HTMLElement>()
const pageEl = ref<HTMLElement>()
const facade = ref<HTMLElement>()
const page = ref<UploadMenuPage>(props.initialPage)
const mounted = ref(false)
const closing = ref(false)
const fromFab = ref(true)

const widthProgress = new Spring(0, QUICK_SPRING)
const heightProgress = new Spring(0, QUICK_SPRING)
const menuScale = new Spring(1, MENU_SPRING)

let contentHeight = 0
let placement: Placement | null = null
let from: Box | null = null
let originElement: HTMLElement | null = null
let pendingAction: (() => void) | null = null
let squeezeStart: number | null = null
let frameId = 0
let lastFrame = 0
let switching = false

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max))

const fabBottom = (rect: DOMRect) =>
  window.innerHeight - rect.bottom - BAR_OVERHANG

const place = () => {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const anchorRect = fromFab.value
    ? null
    : (props.anchor?.getBoundingClientRect() ?? null)
  const fabRect = props.origin?.getBoundingClientRect()
  const left = MARGIN
  const right = vw - MARGIN
  const top = MARGIN
  const barBottom = fabRect ? fabBottom(fabRect) : FALLBACK_BAR_BOTTOM
  const bottom = anchorRect ? vh - MARGIN : vh - barBottom
  const below = anchorRect ? clamp(anchorRect.bottom + GAP, top, bottom) : top
  const aboveBottom = anchorRect
    ? clamp(anchorRect.top - GAP, top, bottom)
    : bottom
  const widthScale = Math.min(1, Math.max(0, right - left) / MENU_WIDTH)
  const above =
    !!anchorRect &&
    contentHeight * widthScale > bottom - below &&
    aboveBottom - top > bottom - below
  const menuBottom = anchorRect ? (above ? aboveBottom : bottom) : bottom
  const available = anchorRect && !above ? bottom - below : menuBottom - top
  const scale = Math.min(widthScale, Math.max(0, available) / contentHeight)
  const width = MENU_WIDTH * scale
  const height = contentHeight * scale
  placement = {
    width,
    height,
    scale,
    above,
    left: clamp(anchorRect?.left ?? (vw - width) / 2, left, right - width),
    top: clamp(
      anchorRect ? (above ? aboveBottom - height : below) : menuBottom - height,
      top,
      bottom - height
    ),
  }

  if (anchorRect && props.anchor) {
    const sliverWidth = Math.min(anchorRect.width, PULL_DOWN_MAX_START_WIDTH)
    from = {
      width: sliverWidth,
      height: 0,
      right: vw - placement.left - sliverWidth,
      bottom: above
        ? vh - placement.top - placement.height
        : vh - placement.top,
      radius: parseFloat(getComputedStyle(props.anchor).borderRadius) || 0,
    }
  } else {
    from = {
      width: fabRect?.width ?? FAB_SIZE,
      height: fabRect?.height ?? FAB_SIZE,
      right: fabRect ? vw - fabRect.right : (vw - 307) / 2,
      bottom: fabRect
        ? vh - fabRect.bottom
        : FALLBACK_BAR_BOTTOM + BAR_OVERHANG,
      radius: (fabRect?.width ?? FAB_SIZE) / 2,
    }
  }
}

const target = () => {
  const p = placement!
  return {
    right: window.innerWidth - p.left - p.width,
    bottom: window.innerHeight - p.top - p.height,
  }
}

const bubbleRadius = (wp: number, hp: number, w: number, h: number) => {
  const settle = Math.max(0, hp) ** 3
  const capsule = Math.max(0, Math.min(w, h)) / 2
  const blob = Math.min(
    capsule,
    capsule + (CARD_RADIUS * placement!.scale - capsule) * settle
  )
  const fromOrigin = clamp(wp / 0.25, 0, 1)
  return from!.radius + (blob - from!.radius) * fromOrigin
}

const render = () => {
  const el = bubble.value
  const inner = content.value
  if (!el || !inner || !placement || !from) return
  const vw = window.innerWidth
  const vh = window.innerHeight
  const wp = widthProgress.value
  const hp = heightProgress.value
  const goal = target()
  const w = Math.max(0, from.width + (placement.width - from.width) * wp)
  const h = Math.max(0, from.height + (placement.height - from.height) * hp)
  const right = fromFab.value
    ? from.right + (goal.right - from.right) * wp
    : vw - placement.left - w
  const bottom = fromFab.value
    ? from.bottom + (goal.bottom - from.bottom) * hp
    : placement.above
      ? goal.bottom
      : vh - placement.top - h
  const radius = bubbleRadius(wp, hp, w, h)

  el.style.left = `${vw - right - w}px`
  el.style.top = `${vh - bottom - h}px`
  el.style.width = `${w}px`
  el.style.height = `${h}px`
  el.style.borderRadius = `${radius}px`
  el.style.transform = `scale(${menuScale.value})`

  const settled = fromFab.value ? clamp(wp / 0.5, 0, 1) : 1
  const lerp = (a: number, b: number) => a + (b - a) * settled
  el.style.boxShadow = `0 ${lerp(3, 8)}px ${lerp(28, 40)}px 0 rgba(0,0,0,${lerp(0.1, 0.12)})`
  el.style.borderColor = `rgba(255,255,255,${0.6 * settled})`

  const anchorRight = from.right < goal.right + placement.width / 2
  const anchorBottom = fromFab.value || placement.above
  inner.style.left = anchorRight ? '' : '0'
  inner.style.right = anchorRight ? '0' : ''
  inner.style.top = anchorBottom ? '' : '0'
  inner.style.bottom = anchorBottom ? '0' : ''
  inner.style.transformOrigin = `${anchorRight ? 'right' : 'left'} ${
    anchorBottom ? 'bottom' : 'top'
  }`
  inner.style.transform = `scale(${w / MENU_WIDTH})`
  inner.style.opacity = `${clamp((wp - 0.08) / 0.42, 0, 1)}`

  if (facade.value) {
    facade.value.style.opacity = `${1 - clamp(wp / 0.5, 0, 1)}`
    facade.value.style.borderRadius = `${radius}px`
  }
}

const stepSqueeze = (now: number, seconds: number) => {
  if (squeezeStart === null) {
    menuScale.step(seconds)
    return
  }
  const elapsed = now - squeezeStart
  if (elapsed < 100) {
    menuScale.set(1 + (0.82 - 1) * easeInOutQuad(elapsed / 100))
  } else if (elapsed < 260) {
    menuScale.set(0.82 + (1.04 - 0.82) * easeInOutQuad((elapsed - 100) / 160))
  } else {
    squeezeStart = null
    menuScale.set(1.04)
    menuScale.target = 1
  }
}

const backAtOrigin = () => {
  if (!placement || !from) return true
  if (!fromFab.value) return heightProgress.value < 0.07
  const goal = target()
  return (
    Math.abs(widthProgress.value) *
      Math.max(
        Math.abs(placement.width - from.width),
        Math.abs(goal.right - from.right)
      ) <=
      1 &&
    Math.abs(heightProgress.value) *
      Math.max(
        Math.abs(placement.height - from.height),
        Math.abs(goal.bottom - from.bottom)
      ) <=
      1
  )
}

const finishClose = () => {
  cancelAnimationFrame(frameId)
  frameId = 0
  if (originElement) originElement.style.visibility = ''
  originElement = null
  mounted.value = false
  closing.value = false
  const action = pendingAction
  pendingAction = null
  action?.()
}

const frame = (now: number) => {
  frameId = 0
  const seconds = Math.min((now - (lastFrame || now)) / 1000 || 1 / 60, 0.05)
  lastFrame = now
  widthProgress.step(seconds)
  heightProgress.step(seconds)
  stepSqueeze(now, seconds)
  render()
  if (closing.value && backAtOrigin()) {
    finishClose()
    return
  }
  const moving =
    !widthProgress.resting ||
    !heightProgress.resting ||
    !menuScale.resting ||
    squeezeStart !== null
  if (moving) frameId = requestAnimationFrame(frame)
  else lastFrame = 0
}

const wake = () => {
  if (!frameId) frameId = requestAnimationFrame(frame)
}

const measure = () => {
  contentHeight = content.value?.offsetHeight ?? contentHeight
  place()
}

const show = async () => {
  if (mounted.value && closing.value) {
    closing.value = false
    pendingAction = null
    widthProgress.target = 1
    heightProgress.target = 1
    wake()
    return
  }
  page.value = props.initialPage
  fromFab.value = !props.anchor
  widthProgress.set(0)
  heightProgress.set(0)
  menuScale.set(1)
  squeezeStart = null
  closing.value = false
  mounted.value = true
  await nextTick()
  measure()
  render()
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      if (!mounted.value || closing.value) return
      if (fromFab.value && props.origin) {
        originElement = props.origin
        originElement.style.visibility = 'hidden'
      }
      widthProgress.target = 1
      heightProgress.target = 1
      lastFrame = 0
      wake()
    })
  )
}

const hide = () => {
  if (!mounted.value || closing.value) return
  closing.value = true
  widthProgress.target = 0
  heightProgress.target = 0
  wake()
}

watch(open, (value) => (value ? show() : hide()))

const close = (action?: () => void) => {
  if (closing.value) return
  if (action) pendingAction = action
  open.value = false
}

const handleRegister = () => {
  close()
  showUploadDialog()
}

const switchPage = (next: UploadMenuPage) => {
  const element = pageEl.value
  if (switching || closing.value || next === page.value || !element) return
  switching = true
  squeezeStart = performance.now()
  wake()
  element.animate(
    [
      { opacity: 1, transform: 'none' },
      { opacity: 0, transform: `translateX(-${PAGE_SLIDE}px)` },
    ],
    { duration: 100, easing: 'cubic-bezier(0.11, 0, 0.5, 0)', fill: 'forwards' }
  ).onfinish = async () => {
    page.value = next
    await nextTick()
    element.getAnimations().forEach((animation) => animation.cancel())
    measure()
    render()
    element.animate(
      [
        { opacity: 0, transform: `translateX(${PAGE_SLIDE}px)` },
        { opacity: 1, transform: 'none' },
      ],
      { duration: 240, easing: 'cubic-bezier(0.33, 1, 0.68, 1)' }
    )
    switching = false
  }
}

useEventListener(window, 'keydown', (event) => {
  if (open.value && (event as KeyboardEvent).key === 'Escape') close()
})
useEventListener(window, 'resize', () => {
  if (!mounted.value) return
  measure()
  render()
})

onBeforeUnmount(() => {
  cancelAnimationFrame(frameId)
  if (originElement) originElement.style.visibility = ''
})
</script>
